import { useEffect, useState } from 'react'
import Nav from '../components/common/Nav'
import Footer from '../components/common/Footer'
import { pushAnalyticsEvent } from '../utils/googleAnalytics'
import { Layout } from '../components/Layout'
import { Image } from '../components/Image'
import Separator from '../components/common/Separator'
import Background from '../images/grey-background.svg'
import BackgroundMobile from '../images/grey-background-mobile.svg'
import BackgroundMobileLg from '../images/grey-background-mobile-lg.svg'
import HomeHero from '../images/svg/homepage-hero.svg'
import SquareCircle from '../images/svg/square-circle.svg'
import BlueTriangle from '../images/svg/blue-triangle.svg'
import LinkButton from '../components/common/LinkButton'
import Testimonial, { TestimonialData } from '../components/common/Testimonial'
import TryAurelius from '../components/common/TryAurelius'
import { debounce } from 'lodash-es'

const testimonial: TestimonialData = {
  text: '"To be honest Box notes and Confluence pages just weren\'t cutting it for us. The ability to tag, and create insights in Aurelius gave me a low effort way to track and reference insights when having product discussions... our team of ~20 designers was looking for a way to keep track of all of the research we\'ve been acquiring."',
  imgFilename: 'John-Pawlenty.png',
  imgAlt: 'John Pawlenty',
  name: 'John Pawlenty',
  job: 'UX Designer at Paylocity',
}
const review: TestimonialData = {
  text: '"Aurelius is a perfect choice for every research team looking for a way to document and store research data securely for years and make it easy to access. I used it together with my team to look for insights across different projects and quickly find the data on a specific research theme."',
  imgFilename: 'Kate-Gross.png',
  imgAlt: 'Kate Gross',
  name: 'Kate Gross',
  job: 'Manager at ',
  jobLink: 'https://fixthephoto.com/aurelius-platform-review.html',
  jobLinkText: 'fixthephoto.com',
}

export { Head } from '../components/common/Head'

const Page = () => {
  useEffect(() => {
    pushAnalyticsEvent('Registration', 'View-Home')
  }, [])

  const [width, setWidth] = useState(0)

  // Check if is mobile
  useEffect(() => {
    const updateWidth = debounce(() => {
      const newWidth = window.innerWidth
      setWidth(newWidth)
    }, 200)
    if (typeof window !== 'undefined') {
      setWidth(window.innerWidth)
      window.addEventListener('resize', updateWidth)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateWidth)
      }
    }
  }, [])

  const isMobileLg = () => width < 720
  const isMobileXs = () => width >= 0 && width <= 420
  // End

  const backgroundImageSrc = isMobileLg()
    ? isMobileXs()
      ? BackgroundMobile
      : BackgroundMobileLg
    : Background

  return (
    <Layout>
      <div className="page-top-border">
        <img
          src={backgroundImageSrc}
          className="grey-background"
          alt="Grey background"
          style={{
            color: 'transparant',
          }}
        />

        <Nav />

        <main>
          <section>
            <div className="row middle-xs home-hero-row fluid-margin-container">
              <div className="col-xs-12 col-md-6">
                <h1 className="home-hero-headline">
                  Your research is
                  <span className="italic"> solid.</span>
                </h1>
                <h1 className="home-hero-headline">
                  But it only matters if you can get from data to insights to
                  influence,
                  <span className="italic"> faster.</span>
                </h1>
                <div className="home-hero-container">
                  <Separator />
                  <h2 className="home-hero-subhead">
                    Prove what you know with the more powerful research
                    repository and insights platform.
                  </h2>

                  <LinkButton
                    url="/free-trial"
                    label="Try free for 30 days"
                    prefetch
                    mobile
                  />

                  <h2 className="home-hero-details">
                    All Features • Full Access • No Credit Card Required
                  </h2>
                </div>
              </div>
              <div className="col-md-6 home-hero-img-container">
                <HomeHero className="data-analysis-img" />
              </div>
            </div>
          </section>

          <section>
            <div className="row center-xs home-product-line">
              <BlueTriangle className="product-triangle" />
              <div className="col-xs-12 col-md-5 col-lg-5 home-product-img-container">
                <SquareCircle className="problems-img" />
              </div>
              <div className="home-point col-xs-12 col-md-7 col-lg-7">
                <h3 className="home-point-headline">
                  We&apos;re researchers, too–we know how frustrating it is not
                  to have the right tools.
                </h3>
                <h3 className="home-point-headline last">You end up…</h3>
                <ul className="bullet-points">
                  <li>
                    Patching together spreadsheets, collaboration tools and list
                    apps never built for the purpose
                  </li>
                  <li>
                    Tinkering with custom solutions that take too long to build
                    and maintain
                  </li>
                  <li>
                    Force-fitting research tools into your process, only to find
                    they&apos;re too complex, too rigid or just not powerful
                    enough (&quot;more work than they&apos;re worth&quot;)
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <Testimonial {...testimonial} tryButton />
          </section>

          <div className="home-testimonial-separator">
            <Separator />
          </div>

          <section>
            <div className="row supporting-container">
              <h3 className="supporting-h3">
                You need a{' '}
                <span className="supporting-h3 brown">
                  research and insights platform
                </span>{' '}
                robust enough to handle your end-to-end process—without any of
                the complexities that slow you down.
              </h3>
            </div>
            <div className="row home-testimoninal-split">
              <div className="col-xs-12 col-md-6 home-testimonial-half">
                <div className="home-testimonial-half-testimonial">
                  <p>
                    &quot;When I saw how easy it was to import hundreds of notes
                    in seconds and how simple it made finding key insights, I
                    knew someone had finally made a tool that simplified and
                    streamlined data synthesis. What I wasn&apos;t ready for,
                    however, was how helpful Aurelius was when it came time to
                    put meaning to the insights and begin formulating the best
                    way to communicate the findings.&quot;
                  </p>
                </div>
                <div className="row center-xs">
                  <div className="col-xs-6 col-md-12 center-xs">
                    <div className="row center-xs">
                      <Image
                        className="t-img"
                        filename="Callie-Blixrud.png"
                        alt="Callie Blixrud"
                      />
                    </div>
                    <div className="row center-xs">
                      <p className="home-testimonial-row-name">
                        Callie Blixrud
                      </p>
                    </div>
                    <div className="row center-xs">
                      <p className="home-testimonial-row-desc">
                        UX Strategist and Researcher
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-12 col-md-6 home-testimonial-half">
                <div className="home-testimonial-half-testimonial">
                  <p>
                    &quot;The tool itself is powerful simplicity - it&apos;s
                    what researchers do already, just, now there&apos;s a tool
                    that does it. For any kind of qualitative research,
                    you&apos;re already doing this somehow. This is a tool that
                    helps you do it better.&quot;
                  </p>
                </div>
                <div className="row center-xs">
                  <div className="col-xs-6 col-md-12 center-xs">
                    <div className="row center-xs">
                      <Image
                        className="t-img"
                        filename="Sean-Connelly.png"
                        alt="Sean Connely"
                      />
                    </div>
                    <div className="row center-xs">
                      <p className="home-testimonial-row-name">Sean Connelly</p>
                    </div>
                    <div className="row center-xs">
                      <p className="home-testimonial-row-desc">
                        User Researcher
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="home-testimonial-separator">
            <Separator />
          </div>
          <section>
            <Testimonial {...review} />
          </section>

          <section>
            <TryAurelius />
          </section>
        </main>
        <Footer />
      </div>
    </Layout>
  )
}

export default Page
